import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { Card, Grid, Tab, Tabs } from "@mui/material";
import { fetchWorkOrders, useWorkOrdersCalendar, useWorkOrdersSummary } from "api/workOrders";
import BaseTable from "components/DataTable/BaseTable";
import HeaderCard from "components/DataTable/HeaderCard";
import ListingImageCol from 'components/ListingImageCol';
import ListingStaffAvatarGroup from 'components/ListingStaffAvatarGroup';
import Loading from "components/Loading";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import PieChart from "examples/Charts/PieChart";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from 'react-dom/server';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useFilterStore from "store/useFilterStore";
import useGlobalStore from "store/useGlobalStore";
import { formatDateTime, getPriorityFilters, getProgressStatusFilters, getWorkOrderStatusFilters, PRIORITY_COLORS, PRIORITY_CRITICAL, PRIORITY_HIGH, PRIORITY_LOW, PRIORITY_MEDIUM, PRIORITY_VERY_LOW, PriorityIcon, PROGRESS_COMPLETED, PROGRESS_IN_PROGRESS, PROGRESS_IN_REVIEW, PROGRESS_ON_HOLD, PROGRESS_OPEN, PROGRESS_STATUS_ICON, PROGRESS_WAITING_COST, PROGRESS_WAITING_INVOICE, PROGRESS_WAITING_PARTS, STATUS_COMPLETED, STATUS_PENDING, STATUS_REJECTED, WorkOrderType } from 'utils/helper';

// Colors to differentiate date types on calendar (created_at, due_date, start_date)
const DATE_TYPE_COLORS = {
    start_date: '#1976d2',   // blue
    due_date: '#d32f2f',     // red
    completed_at: '#4caf50',   // green
};

const TABLE_COLUMNS = [
    { id: "title", Header: "Title", accessor: "title", align: "left", sortable: true },
    { id: "progress_status", Header: "Progress Status", accessor: "progress_status", align: "left" },
    { id: "status", Header: "Status", accessor: "status", align: "left" },
    { id: "start_date", Header: "Start Date", accessor: "start_date", align: "left", sortable: true },
    { id: "due_date", Header: "Due Date", accessor: "due_date", align: "left", sortable: true },
    { id: "assigned_user", Header: "Assigned To", accessor: "assigned_user", align: "left" },
    { id: "category", Header: "Category", accessor: "category", align: "left" },
    { id: "priority", Header: "Priority", accessor: "priority", align: "left" },
    { id: "work_type", Header: "Work Type", accessor: "work_type", align: "left" },
    { id: "user", Header: "Created By", accessor: "user", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
    { id: "updated_at", Header: "Updated At", accessor: "updated_at", align: "left", sortable: true },
];

export const parentRoute = '/work-orders';
const statusSelection = getWorkOrderStatusFilters()
const prioritySelection = getPriorityFilters()
const progressStatusSelection = getProgressStatusFilters()
const workOrderTypeSelection = WorkOrderType ? Object.keys(WorkOrderType).map(key => ({ value: WorkOrderType[key], label: key.charAt(0) + key.slice(1).toLowerCase() })) : [];

function WorkOrdersTable() {
    const { getFilters, setFilters } = useFilterStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showKPI, setPrevSelectedRow } = useGlobalStore();
    const [refreshKey] = useState(0);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'calendar' ? 1 : 0);
    const [currentDate, setCurrentDate] = useState(location.state?.date ?? new Date());
    const [params, setParams] = useState({});
    const [baseTableFilters, setBaseTableFilters] = useState({ sort: 'created_at', dir: 'desc' });

    const summaryQuery = useWorkOrdersSummary({}, {
        enabled: showKPI,
    });

    const calendarQuery = useWorkOrdersCalendar({
        start_date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split("T")[0],
        end_date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString().split("T")[0],
        ...params
    }, {
        enabled: activeTab === 1,
    });

    const loading = summaryQuery.isFetching || calendarQuery.isFetching;
    const summary = useMemo(() => summaryQuery.data || {}, [summaryQuery.data]);
    const calendarEvents = useMemo(() => {
        const data = calendarQuery.data || [];
        const events = [];

        data.forEach((dayData) => {
            if (dayData.items && dayData.items.length > 0) {
                dayData.items.forEach((item) => {
                    events.push({
                        id: item.id,
                        title: item.title,
                        date: dayData.date,
                        display: 'block',
                        backgroundColor: DATE_TYPE_COLORS[item.date_type] || '#455a64',
                        borderColor: DATE_TYPE_COLORS[item.date_type] || '#37474f',
                        textColor: '#fff',
                        extendedProps: {
                            workOrderId: item.id,
                            dateType: item.date_type,
                            priorityId: item.priority,
                            priorityName: item.priority_name,
                            progressId: item.progress_status,
                            progressStatus: item.progress_status_name,
                            workType: item.work_type?.label || item.work_type?.name,
                        },
                    });
                });
            }
        });

        return events;
    }, [calendarQuery.data]);
    const currentPath = window.location.pathname;

    useEffect(() => {
        const record = getFilters(currentPath);
        if (record?.customParams) {
            setParams(record.customParams);
        }
        if (record?.baseTable) {
            setBaseTableFilters(record.baseTable);
        }
        return () => {
            setPrevSelectedRow({});
        };
    }, [currentPath, getFilters, setPrevSelectedRow]);

    // Persist custom filters whenever they change (merge with existing baseTable state)
    useEffect(() => {
        const record = getFilters(currentPath);
        setFilters(currentPath, {
            baseTable: record.baseTable, // preserve the table paging/sort managed by BaseTable
            customParams: params
        });
    }, [params, currentPath, getFilters, setFilters]);

    const calendarRef = useRef(null)

    const createTableRow = useCallback((item) => ({
        title: <ListingImageCol imgUrl={item.img_url} name={item.title} />,
        status: <MDTypography component="div" variant="standard" color={item.status?.id === STATUS_PENDING ? "warning" : item.status?.id === STATUS_REJECTED ? "error" : item.status?.id === STATUS_COMPLETED ? "success" : "info"}>
            <b>{item.status?.name}</b>
        </MDTypography>,
        priority: item.priority?.id ? <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, color: PRIORITY_COLORS[item.priority?.id] }}>
            {PriorityIcon(item.priority?.id)}
            <MDTypography component="div" variant="standard">
                {item.priority?.name}
            </MDTypography>
        </MDBox> : '-',
        progress_status: <MDTypography component="div" variant="standard">{PROGRESS_STATUS_ICON[item.progress_status?.id]} {item.progress_status?.name}</MDTypography>,
        start_date: <MDTypography component="div" variant="standard" color="text">{item.start_date ? new Date(item.start_date).toLocaleDateString() : '-'}</MDTypography>,
        due_date: <MDTypography component="div" variant="standard" color="text">
            {item.due_date ? new Date(item.due_date).toLocaleDateString() : '-'}
        </MDTypography>,
        assigned_user: <ListingStaffAvatarGroup staff={item.assigned_users?.length > 0 ? item.assigned_users : []} />,
        category: <MDTypography component="div" variant="standard" color="text">{item.category || '-'}</MDTypography>,
        work_type: <MDTypography component="div" variant="standard" color="text">{item.work_type?.label || '-'}</MDTypography>,
        user: <MDTypography component="div" variant="standard" color="text">{item.user?.full_name}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">{formatDateTime(item.created_at)}</MDTypography>,
        updated_at: <MDTypography component="div" variant="standard" color="text">{formatDateTime(item.updated_at)}</MDTypography>,
    }), []);

    const filterConfigs = [
        {
            id: 'work_type',
            label: 'Work Type',
            type: 'select',
            options: workOrderTypeSelection,
            placeholder: 'All Work Types',
        },
        {
            id: 'priority',
            label: 'Priority',
            type: 'select',
            options: prioritySelection,
            placeholder: 'All Priorities',
        },
        {
            id: 'progress_status',
            label: 'Progress Status',
            type: 'select',
            options: progressStatusSelection,
            placeholder: 'All Progress Status',
        },
        {
            id: 'status',
            label: 'Status',
            type: 'select',
            options: statusSelection,
            placeholder: 'All Status',
        }
    ];

    // Progress Status Pie Chart Data
    const progressChartData = useMemo(() => {
        if (!summary?.progress_breakdown) {
            return {
                labels: [],
                datasets: {
                    label: "Work Orders",
                    data: [],
                    backgroundColors: [],
                }
            };
        }

        const labels = summary.progress_breakdown.map(item => item.name);
        const data = summary.progress_breakdown.map(item => item.count);
        const backgroundColors = summary.progress_breakdown.map(item => {
            switch (item.id) {
                case PROGRESS_COMPLETED: return 'success';
                case PROGRESS_IN_PROGRESS: return 'info';
                case PROGRESS_ON_HOLD: return 'warning';
                case PROGRESS_OPEN: return 'primary';
                case PROGRESS_IN_REVIEW: return 'secondary';
                case PROGRESS_WAITING_PARTS: return 'error';
                case PROGRESS_WAITING_COST: return 'dark';
                case PROGRESS_WAITING_INVOICE: return 'light';
                default: return 'dark';
            }
        });

        return {
            labels,
            datasets: {
                label: "Work Orders",
                data,
                backgroundColors,
            }
        };
    }, [summary]);

    // Priority Pie Chart Data
    const priorityChartData = useMemo(() => {
        if (!summary?.priority_breakdown) {
            return {
                labels: [],
                datasets: {
                    label: "Work Orders",
                    data: [],
                    backgroundColors: [],
                }
            };
        }

        const labels = summary.priority_breakdown.map(item => item.name);
        const data = summary.priority_breakdown.map(item => item.count);
        const backgroundColors = summary.priority_breakdown.map(item => {
            switch (item.id) {
                case PRIORITY_CRITICAL: return 'error';
                case PRIORITY_HIGH: return 'warning';
                case PRIORITY_MEDIUM: return 'info';
                case PRIORITY_LOW: return 'success';
                case PRIORITY_VERY_LOW: return 'secondary';
                default: return 'dark';
            }
        });

        return {
            labels,
            datasets: {
                label: "Work Orders",
                data,
                backgroundColors,
            }
        };
    }, [summary]);

    const summaryChartData = useMemo(() => {

        return (
            showKPI && <MDBox mt={2} mb={3}>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                        <Loading size={50} />
                    </MDBox>
                ) : (
                    <>
                        {/* Key Metrics Row */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} md={3}>
                                <ComplexStatisticsCard
                                    icon="engineering"
                                    title="Total Work Orders"
                                    color="primary"
                                    count={summary?.total || 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <ComplexStatisticsCard
                                    icon="schedule"
                                    title="Overdue"
                                    color="error"
                                    count={summary?.overdue || 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <ComplexStatisticsCard
                                    icon="today"
                                    title="Due Today"
                                    color="warning"
                                    count={summary?.due_today || 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <ComplexStatisticsCard
                                    icon="assignment_ind"
                                    title="Unassigned Open"
                                    color="info"
                                    count={summary?.unassigned_open || 0}
                                />
                            </Grid>
                        </Grid>

                        {/* Pie Charts Row */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} md={6}>
                                <PieChart
                                    icon={{ color: "info", component: "track_changes" }}
                                    title="Progress Status Distribution"
                                    description="Work orders breakdown by progress status"
                                    height="18rem"
                                    chart={progressChartData}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <PieChart
                                    icon={{ color: "warning", component: "priority_high" }}
                                    title="Priority Distribution"
                                    description="Work orders breakdown by priority level"
                                    height="18rem"
                                    chart={priorityChartData}
                                />
                            </Grid>
                        </Grid>

                        {/* Additional Metrics */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <ComplexStatisticsCard
                                    icon="check_circle"
                                    title="Completed Last 7 Days"
                                    color="success"
                                    count={summary?.completed_last_7_days || 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ComplexStatisticsCard
                                    icon="timer"
                                    title="Avg Completion Time"
                                    color="dark"
                                    count={
                                        summary?.avg_completion_hours_recent
                                            ? `${Math.round(summary.avg_completion_hours_recent)}h`
                                            : 'N/A'
                                    }
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </MDBox>)
    }, [summary, showKPI, loading, progressChartData, priorityChartData]);

    return (
        <DashboardLayout>
            <DashboardNavbar hasKPI />

            {summaryChartData}

            <MDBox pt={6} pb={3}>
                <Card style={{ width: '100%' }}>
                    <HeaderCard title="Work Orders Calendar"
                        showButton
                        buttonText="Add Work Order"
                        onButtonClick={() => navigate(`${parentRoute}/add-work-order?${searchParams.toString()}`, { state: { ...baseTableFilters, date: currentDate } })}
                    />

                    {/* Tabs Interface */}
                    <MDBox pt={3} px={3}>
                        <Tabs
                            value={activeTab}
                            onChange={(event, newValue) => { setActiveTab(newValue); setSearchParams({ tab: newValue === 0 ? 'list' : 'calendar' }); }}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="List" />
                            <Tab label="Calendar" />
                        </Tabs>
                        {/* {filtersUI && <MDBox sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 4 }}>
                            {filtersUI}
                        </MDBox>} */}
                    </MDBox>

                    <MDBox mb={3} px={2}>
                        {/* Tab Content */}
                        <MDBox sx={{ display: activeTab === 0 ? 'block' : 'none' }} mt={-2}>
                            <BaseTable
                                key={refreshKey}
                                queryKey={['workOrders']}
                                title="Work Orders"
                                columns={TABLE_COLUMNS}
                                fetchData={fetchWorkOrders}
                                filterConfigs={filterConfigs}
                                onParamsChange={(nextParams) => {
                                    const { page, per_page, sort, dir, ...customParams } = nextParams;
                                    setBaseTableFilters(nextParams);
                                    setParams(customParams);
                                }}
                                defaultState={{ ...baseTableFilters, ...params }}
                                createTableRow={createTableRow}
                                tableOnly
                                showSearch
                                searchPlaceholder="Search work order..."
                                onClickRow={(row, key) => { navigate(`${parentRoute}/${row.id}`, { state: baseTableFilters }) }}
                            />
                        </MDBox>

                        {activeTab === 1 &&
                            <MDBox py={3} px={1}>
                                {/* Legend */}
                                <MDBox display="flex" gap={5} flexWrap="wrap" mb={2}>
                                    <MDBox display="flex" gap={2} flexWrap="wrap">
                                        {Object.entries(DATE_TYPE_COLORS).map(([key, color]) => (
                                            <MDBox key={key} display="flex" alignItems="center" gap={1}>
                                                <MDBox sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: color, border: '1px solid rgba(0,0,0,0.2)' }} />
                                                <MDTypography variant="caption" textTransform="capitalize">
                                                    {key.replace('_', ' ')}
                                                </MDTypography>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1} >
                                        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {prioritySelection.map(level => (
                                                <MDBox key={level.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: PRIORITY_COLORS[level.label] }}>
                                                    {PriorityIcon(level.value)}
                                                    <MDTypography variant="caption" >
                                                        {prioritySelection.find(p => p.value === level.value)?.label || ''}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </MDBox>
                                <FullCalendar
                                    ref={calendarRef}
                                    plugins={[dayGridPlugin]}
                                    initialView="dayGridMonth"
                                    events={calendarEvents}
                                    eventDisplay="block"
                                    dayMaxEvents={false}
                                    initialDate={new Date(currentDate)}
                                    eventContent={(arg) => {
                                        const { extendedProps, title } = arg.event;
                                        const priorityIcon = extendedProps.priorityId ? PriorityIcon(extendedProps.priorityId) : null;
                                        const progress = extendedProps.progressStatus ? `${PROGRESS_STATUS_ICON[extendedProps.progressId]} ${extendedProps.progressStatus}` : '';
                                        return {
                                            domNodes: [
                                                (() => {
                                                    const container = document.createElement('div');
                                                    container.style.display = 'flex';
                                                    container.style.flexDirection = 'column';
                                                    container.style.fontSize = '0.65rem';
                                                    container.style.lineHeight = '1.1';
                                                    container.style.fontWeight = '500';
                                                    container.style.gap = '2px';

                                                    const row1 = document.createElement('div');
                                                    row1.style.display = 'flex';
                                                    row1.style.alignItems = 'center';
                                                    row1.style.gap = '2px';
                                                    row1.style.marginBottom = '1px';

                                                    if (priorityIcon) {
                                                        const spanIcon = document.createElement('span');
                                                        spanIcon.style.display = 'flex';
                                                        spanIcon.style.alignItems = 'center';
                                                        spanIcon.style.fontSize = '0.8rem';
                                                        // Render React icon to static markup (SVG) so it displays correctly
                                                        spanIcon.innerHTML = ReactDOMServer.renderToStaticMarkup(priorityIcon);
                                                        row1.appendChild(spanIcon);
                                                    }
                                                    const titleSpan = document.createElement('span');
                                                    titleSpan.textContent = title;
                                                    titleSpan.style.display = '-webkit-box';
                                                    titleSpan.style.webkitLineClamp = '2';
                                                    titleSpan.style.webkitBoxOrient = 'vertical';
                                                    titleSpan.style.overflow = 'hidden';
                                                    titleSpan.style.textOverflow = 'ellipsis';
                                                    titleSpan.style.lineHeight = '1.2';
                                                    titleSpan.style.maxHeight = '2.4em';
                                                    titleSpan.style.wordBreak = 'break-word';
                                                    titleSpan.style.whiteSpace = 'normal';
                                                    titleSpan.style.flex = '1';
                                                    row1.appendChild(titleSpan);
                                                    container.appendChild(row1);
                                                    const row2 = document.createElement('div');
                                                    row2.textContent = `${progress}`;
                                                    row2.style.marginTop = '1px';
                                                    container.appendChild(row2);
                                                    return container;
                                                })()
                                            ]
                                        };
                                    }}
                                    headerToolbar={{
                                        left: 'title',
                                        right: 'today customPrev customNext'
                                    }}
                                    customButtons={{
                                        today: {
                                            text: 'Today', click: function () {
                                                const calendarApi = calendarRef.current.getApi()
                                                calendarApi.today();
                                                const currentDate = calendarApi.getDate();
                                                setCurrentDate(currentDate);
                                            }
                                        },
                                        customPrev: {
                                            icon: 'chevron-left',
                                            click: function () {
                                                const calendarApi = calendarRef.current.getApi()
                                                calendarApi.prev();
                                                const currentDate = calendarApi.getDate();
                                                setCurrentDate(currentDate);
                                            }
                                        },
                                        customNext: {
                                            icon: 'chevron-right',
                                            click: function () {
                                                const calendarApi = calendarRef.current.getApi()
                                                calendarApi.next();
                                                const currentDate = calendarApi.getDate();
                                                setCurrentDate(currentDate);
                                            }
                                        }
                                    }}
                                    eventClick={(info) => {
                                        navigate(`${parentRoute}/${info.event.extendedProps.workOrderId}?${searchParams.toString()}`, { state: { ...baseTableFilters, date: currentDate } });
                                    }}
                                />
                            </MDBox>}
                    </MDBox>
                </Card>
            </MDBox>
        </DashboardLayout>
    );
}

export default WorkOrdersTable;